import { useConfirm } from '@/components/Confirm';
import { Modal } from '@/components/Modal';
import {
  ApiError,
  deleteCancellationPolicy,
  getCancellationPolicies,
  listServices,
  upsertCancellationPolicy,
} from '@/lib/api';
import type {
  CancellationPoliciesResponse,
  CancellationPolicy,
  CancellationPolicyShape,
  LadderEntry,
  ServiceCatalogueItem,
} from '@/lib/types';
import { ArrowDown, ArrowUp, CalendarX, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseLadder(json: string): LadderEntry[] {
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is LadderEntry =>
        e !== null &&
        typeof e === 'object' &&
        typeof e.hours_before === 'number' &&
        typeof e.refund_pct === 'number',
    );
  } catch {
    return [];
  }
}

function policyShape(policy: CancellationPolicy): CancellationPolicyShape {
  return {
    ladder: parseLadder(policy.ladderJson),
    no_show_refund_pct: policy.noShowRefundPct,
  };
}

function formatHours(hours: number): string {
  if (hours === 0) return 'less than 24 hours';
  if (hours % 24 === 0 && hours >= 24) {
    const days = hours / 24;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

interface ServiceKindGuess {
  kind: 'boarding' | 'daycare' | 'grooming' | 'other';
}

function guessKind(service: ServiceCatalogueItem): ServiceKindGuess['kind'] {
  const cat = service.category.toLowerCase();
  if (cat.includes('boarding')) return 'boarding';
  if (cat.includes('daycare')) return 'daycare';
  if (cat.includes('grooming')) return 'grooming';
  return 'other';
}

// ---------------------------------------------------------------------------
// Editor drawer
// ---------------------------------------------------------------------------

interface EditorDrawerProps {
  title: string;
  serviceId: string | null;
  initialLadder: LadderEntry[];
  initialNoShowRefundPct: number;
  onClose: () => void;
  onSaved: () => void;
}

interface DraftEntry {
  /** Stable client-only id so React keys survive reorder/insert/delete. */
  uid: string;
  hours_before: string;
  refund_pct: string;
}

let draftUidSeq = 0;
function newDraftUid(): string {
  draftUidSeq += 1;
  return `d${draftUidSeq}`;
}

function entryToDraft(entry: LadderEntry): DraftEntry {
  return {
    uid: newDraftUid(),
    hours_before: String(entry.hours_before),
    refund_pct: String(entry.refund_pct),
  };
}

function emptyDraft(): DraftEntry {
  return { uid: newDraftUid(), hours_before: '', refund_pct: '' };
}

function buildLadder(
  draft: DraftEntry[],
): { ok: true; ladder: LadderEntry[] } | { ok: false; error: string } {
  if (draft.length === 0) {
    return { ok: false, error: 'Ladder must have at least one row' };
  }
  const out: LadderEntry[] = [];
  let lastHours = Number.POSITIVE_INFINITY;
  for (let i = 0; i < draft.length; i++) {
    const row = draft[i];
    if (!row) continue;
    const hb = Number(row.hours_before);
    const rp = Number(row.refund_pct);
    if (!Number.isInteger(hb) || hb < 0) {
      return { ok: false, error: `Row ${i + 1}: hours_before must be a non-negative integer` };
    }
    if (!Number.isInteger(rp) || rp < 0 || rp > 100) {
      return { ok: false, error: `Row ${i + 1}: refund_pct must be an integer 0-100` };
    }
    if (hb >= lastHours) {
      return {
        ok: false,
        error: `Row ${i + 1}: hours_before must be strictly less than row ${i} (was ${hb}, must be < ${lastHours})`,
      };
    }
    lastHours = hb;
    out.push({ hours_before: hb, refund_pct: rp });
  }
  return { ok: true, ladder: out };
}

function EditorDrawer({
  title,
  serviceId,
  initialLadder,
  initialNoShowRefundPct,
  onClose,
  onSaved,
}: EditorDrawerProps) {
  const [draft, setDraft] = useState<DraftEntry[]>(() =>
    initialLadder.length > 0 ? initialLadder.map(entryToDraft) : [emptyDraft()],
  );
  const [noShowText, setNoShowText] = useState<string>(String(initialNoShowRefundPct));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<string[] | null>(null);

  function updateRow(index: number, patch: Partial<DraftEntry>) {
    setDraft((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function addRow() {
    setDraft((prev) => [...prev, emptyDraft()]);
  }

  function removeRow(index: number) {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  }

  function moveUp(index: number) {
    if (index <= 0) return;
    setDraft((prev) => {
      const next = prev.slice();
      const cur = next[index];
      const before = next[index - 1];
      if (cur && before) {
        next[index - 1] = cur;
        next[index] = before;
      }
      return next;
    });
  }

  function moveDown(index: number) {
    setDraft((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = prev.slice();
      const cur = next[index];
      const after = next[index + 1];
      if (cur && after) {
        next[index + 1] = cur;
        next[index] = after;
      }
      return next;
    });
  }

  async function handleSave() {
    setBusy(true);
    setError(null);
    setServerIssues(null);

    const ladderResult = buildLadder(draft);
    if (!ladderResult.ok) {
      setError(ladderResult.error);
      setBusy(false);
      return;
    }
    const noShow = Number(noShowText);
    if (!Number.isInteger(noShow) || noShow < 0 || noShow > 100) {
      setError('No-show refund must be an integer 0-100');
      setBusy(false);
      return;
    }

    try {
      await upsertCancellationPolicy({
        serviceId,
        ladder: ladderResult.ladder,
        noShowRefundPct: noShow,
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        const issues = (err.body as { issues?: unknown } | null)?.issues;
        if (Array.isArray(issues) && issues.every((s) => typeof s === 'string')) {
          setServerIssues(issues as string[]);
        } else {
          setError(err.code);
        }
      } else {
        setError('Something went wrong');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" aria-label={title}>
      <div className="flex flex-col p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        <p className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Refund ladder
        </p>
        <p className="mb-3 text-xs text-muted-foreground">
          Each row applies "more than X hours before check-in → N% refund". Rows must be strictly
          decreasing by hours.
        </p>

        <div className="space-y-2">
          {draft.map((row, idx) => (
            <div key={row.uid} className="grid grid-cols-[1fr_1fr_auto_auto_auto] items-end gap-2">
              <div>
                {idx === 0 && (
                  <label
                    htmlFor={`hb-${idx}`}
                    className="mb-1 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Hours before
                  </label>
                )}
                <input
                  id={`hb-${idx}`}
                  type="number"
                  min={0}
                  step={1}
                  value={row.hours_before}
                  onChange={(e) => updateRow(idx, { hours_before: e.target.value })}
                  aria-label={`Row ${idx + 1} hours before`}
                  className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <div>
                {idx === 0 && (
                  <label
                    htmlFor={`rp-${idx}`}
                    className="mb-1 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Refund %
                  </label>
                )}
                <input
                  id={`rp-${idx}`}
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={row.refund_pct}
                  onChange={(e) => updateRow(idx, { refund_pct: e.target.value })}
                  aria-label={`Row ${idx + 1} refund percent`}
                  className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <button
                type="button"
                onClick={() => moveUp(idx)}
                disabled={idx === 0}
                title="Move up"
                aria-label={`Move row ${idx + 1} up`}
                className="rounded-md p-2 text-muted-foreground hover:text-foreground press disabled:opacity-30"
              >
                <ArrowUp className="size-3.5" strokeWidth={1.75} />
              </button>
              <button
                type="button"
                onClick={() => moveDown(idx)}
                disabled={idx === draft.length - 1}
                title="Move down"
                aria-label={`Move row ${idx + 1} down`}
                className="rounded-md p-2 text-muted-foreground hover:text-foreground press disabled:opacity-30"
              >
                <ArrowDown className="size-3.5" strokeWidth={1.75} />
              </button>
              <button
                type="button"
                onClick={() => removeRow(idx)}
                title="Remove row"
                aria-label={`Remove row ${idx + 1}`}
                className="rounded-md p-2 text-muted-foreground hover:text-destructive press"
              >
                <Trash2 className="size-3.5" strokeWidth={1.75} />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addRow}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-primary hover:bg-secondary press"
        >
          <Plus className="size-3.5" strokeWidth={1.75} />
          Add row
        </button>

        <div className="mt-6">
          <label
            htmlFor="no-show-pct"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            No-show refund %
          </label>
          <input
            id="no-show-pct"
            type="number"
            min={0}
            max={100}
            step={1}
            value={noShowText}
            onChange={(e) => setNoShowText(e.target.value)}
            className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {error !== null && <p className="mt-4 text-sm text-destructive">{error}</p>}
        {serverIssues !== null && (
          <ul className="mt-4 space-y-1 text-sm text-destructive">
            {serverIssues.map((iss) => (
              <li key={iss}>• {iss}</li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleSave()}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save policy'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Ladder summary block — read-only
// ---------------------------------------------------------------------------

interface LadderSummaryProps {
  shape: CancellationPolicyShape;
}

function LadderSummary({ shape }: LadderSummaryProps) {
  return (
    <ul className="space-y-1 text-sm">
      {shape.ladder.map((entry) => (
        // hours_before is unique within a ladder (strictly decreasing per spec §7.5).
        <li key={`hb-${entry.hours_before}`} className="flex items-baseline justify-between gap-3">
          <span className="text-muted-foreground">
            More than <span className="num">{formatHours(entry.hours_before)}</span> before
          </span>
          <span className="num font-medium text-foreground">{entry.refund_pct}% refund</span>
        </li>
      ))}
      <li className="flex items-baseline justify-between gap-3 pt-1 border-t border-border mt-2">
        <span className="text-muted-foreground">No-show</span>
        <span className="num font-medium text-foreground">{shape.no_show_refund_pct}% refund</span>
      </li>
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type EditorTarget =
  | { kind: 'closed' }
  | { kind: 'account_default' }
  | { kind: 'service'; serviceId: string; serviceName: string };

export function CancellationPolicySettings() {
  const [data, setData] = useState<CancellationPoliciesResponse | null>(null);
  const [services, setServices] = useState<ServiceCatalogueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [editor, setEditor] = useState<EditorTarget>({ kind: 'closed' });
  const { confirm, ConfirmDialog } = useConfirm();

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const [policies, svcs] = await Promise.all([
        getCancellationPolicies(),
        listServices().catch(() => [] as ServiceCatalogueItem[]),
      ]);
      setData(policies);
      setServices(svcs);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setPermissionDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const overridesByServiceId = useMemo(() => {
    const map = new Map<string, CancellationPolicy>();
    for (const o of data?.overrides ?? []) {
      if (o.serviceId !== null) map.set(o.serviceId, o);
    }
    return map;
  }, [data?.overrides]);

  function shapeForService(service: ServiceCatalogueItem): {
    shape: CancellationPolicyShape;
    source: 'override' | 'account' | 'platform';
  } {
    const override = overridesByServiceId.get(service.id);
    if (override) return { shape: policyShape(override), source: 'override' };
    if (data?.account_default) {
      return { shape: policyShape(data.account_default), source: 'account' };
    }
    const kind = guessKind(service);
    const platform = data?.defaults_by_kind[kind];
    if (platform) return { shape: platform, source: 'platform' };
    return { shape: { ladder: [], no_show_refund_pct: 0 }, source: 'platform' };
  }

  async function handleResetServiceToDefault(policy: CancellationPolicy) {
    const yes = await confirm({
      title: 'Reset to account default?',
      message: 'This service will inherit the account default cancellation policy.',
      confirmLabel: 'Reset',
    });
    if (!yes) return;
    try {
      await deleteCancellationPolicy(policy.id);
      void refresh();
    } catch {
      void refresh();
    }
  }

  async function handleResetAccountDefault(policy: CancellationPolicy) {
    const yes = await confirm({
      title: 'Reset account default?',
      message:
        'Each service will fall through to platform defaults for its kind unless it has a per-service override.',
      confirmLabel: 'Reset',
      danger: true,
    });
    if (!yes) return;
    try {
      await deleteCancellationPolicy(policy.id);
      void refresh();
    } catch {
      void refresh();
    }
  }

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to view cancellation policies
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Cancellation policy</h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
          Refund ladder applied when a customer cancels a booking. The first matching row wins;
          per-service policies override the account default.
        </p>
      </header>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-2xl bg-secondary"
              style={{ opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      ) : (
        <>
          {/* Account default */}
          <section className="mb-8 rounded-2xl bg-surface p-6 ring-1 ring-border elevate">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-heading text-lg font-medium">Account default</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Applied to every service that doesn't have a per-service override.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {data?.account_default && (
                  <button
                    type="button"
                    onClick={() =>
                      data.account_default && handleResetAccountDefault(data.account_default)
                    }
                    className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:text-destructive press"
                  >
                    Reset to platform default
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setEditor({ kind: 'account_default' })}
                  className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press"
                >
                  Edit
                </button>
              </div>
            </div>
            {data?.account_default ? (
              <LadderSummary shape={policyShape(data.account_default)} />
            ) : (
              <p className="text-sm italic text-muted-foreground">
                Using platform defaults — boarding fall-through ladder.
              </p>
            )}
          </section>

          {/* Per-service overrides */}
          <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
            <header className="flex items-center justify-between border-b border-border px-6 py-4">
              <div>
                <h2 className="font-heading text-lg font-medium">Per-service overrides</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Override the account default for individual services.
                </p>
              </div>
            </header>
            {services.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <CalendarX
                  className="mx-auto mb-3 size-6 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <p className="text-sm font-medium text-foreground">No services configured.</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Add services in /settings/services to configure per-service policies.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {services.map((service) => {
                  const resolved = shapeForService(service);
                  const override = overridesByServiceId.get(service.id);
                  return (
                    <li key={service.id} className="px-6 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-foreground">{service.name}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {resolved.source === 'override'
                              ? 'Per-service override'
                              : resolved.source === 'account'
                                ? 'Account default'
                                : 'Platform default'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {override && (
                            <button
                              type="button"
                              onClick={() => void handleResetServiceToDefault(override)}
                              className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:text-destructive press"
                            >
                              Reset to platform default
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              setEditor({
                                kind: 'service',
                                serviceId: service.id,
                                serviceName: service.name,
                              })
                            }
                            className="rounded-md bg-secondary px-3 py-1.5 text-sm font-medium text-foreground press"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                      <div className="mt-3">
                        <LadderSummary shape={resolved.shape} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}

      {/* Editor */}
      {editor.kind === 'account_default' && data && (
        <EditorDrawer
          title="Edit account default cancellation policy"
          serviceId={null}
          initialLadder={data.account_default ? policyShape(data.account_default).ladder : []}
          initialNoShowRefundPct={data.account_default?.noShowRefundPct ?? 0}
          onClose={() => setEditor({ kind: 'closed' })}
          onSaved={() => {
            setEditor({ kind: 'closed' });
            void refresh();
          }}
        />
      )}
      {editor.kind === 'service' &&
        data &&
        (() => {
          const service = services.find((s) => s.id === editor.serviceId);
          if (!service) return null;
          const resolved = shapeForService(service);
          return (
            <EditorDrawer
              title={`Edit cancellation policy — ${editor.serviceName}`}
              serviceId={editor.serviceId}
              initialLadder={resolved.shape.ladder}
              initialNoShowRefundPct={resolved.shape.no_show_refund_pct}
              onClose={() => setEditor({ kind: 'closed' })}
              onSaved={() => {
                setEditor({ kind: 'closed' });
                void refresh();
              }}
            />
          );
        })()}
    </div>
  );
}
